import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import RolePermissionsPicker from '../../components/roles/RolePermissionsPicker';
import {
  useGetRoleByIdQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useLazyGetAllPermissionsQuery,
} from '../../store/api';
import { toast } from 'react-toastify';
import { useServerValidationErrors } from '../../hooks/useServerValidationErrors';
import { extractRolePermissionIds, GUARD_NAME_OPTIONS } from './roleApiHelpers';
import { ROLES_LIST_PATH } from '../../navigations/path';

const CreateRole = () => {
  const { id: editId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(editId);

  const [submitting, setSubmitting] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  const [name, setName] = useState('');
  const [guardName, setGuardName] = useState('user');
  const [allPermissions, setAllPermissions] = useState([]);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [permissionSearch, setPermissionSearch] = useState('');
  const [hydrated, setHydrated] = useState(false);
  const { errors, setErrors, applyApiError, clearError, fieldProps } =
    useServerValidationErrors();

  const [fetchAllPermissionsTrigger] = useLazyGetAllPermissionsQuery();
  const {
    data: roleData,
    isLoading: roleLoading,
    isError: roleError,
    error: roleErr,
  } = useGetRoleByIdQuery(editId, { skip: !isEdit || !editId });
  const [createRole] = useCreateRoleMutation();
  const [updateRole] = useUpdateRoleMutation();

  const loadingPage = isEdit && (roleLoading || !hydrated);

  const fetchAllPermissions = useCallback(async () => {
    setPermissionsLoading(true);
    try {
      const response = await fetchAllPermissionsTrigger().unwrap();
      const list = Array.isArray(response) ? response : (response?.data ?? []);
      setAllPermissions(Array.isArray(list) ? list : []);
    } catch {
      toast.error('Could not load permissions');
      setAllPermissions([]);
    } finally {
      setPermissionsLoading(false);
    }
  }, [fetchAllPermissionsTrigger]);

  useEffect(() => {
    fetchAllPermissions();
  }, [fetchAllPermissions]);

  useEffect(() => {
    if (roleError) {
      toast.error(roleErr?.data?.message || 'Failed to load role');
      navigate(ROLES_LIST_PATH);
    }
  }, [roleError, roleErr, navigate]);

  useEffect(() => {
    if (!isEdit || !roleData || hydrated) return;
    const role = roleData?.data ?? roleData;
    setName(role?.name ?? '');
    setGuardName(role?.guard_name ?? 'user');
    setSelectedPermissionIds(extractRolePermissionIds(roleData));
    setHydrated(true);
  }, [isEdit, roleData, hydrated]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();

    const clientErrors = {};
    if (!trimmedName) clientErrors.name = 'Role name is required';
    if (!guardName.trim()) clientErrors.guard_name = 'Guard name is required';
    setErrors(clientErrors);
    if (Object.keys(clientErrors).length > 0) return;

    const payload = {
      name: trimmedName,
      guard_name: guardName.trim(),
      permissions: selectedPermissionIds,
    };

    setSubmitting(true);
    try {
      if (isEdit) {
        await updateRole({ id: editId, ...payload }).unwrap();
        toast.success('Role updated');
      } else {
        await createRole(payload).unwrap();
        toast.success('Role added');
      }
      navigate(ROLES_LIST_PATH);
    } catch (err) {
      applyApiError(err, isEdit ? 'Failed to update role' : 'Failed to add role');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingPage) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(ROLES_LIST_PATH)} sx={{ mb: 2 }}>
          Back to roles
        </Button>

        <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
          {isEdit ? 'Edit role' : 'Add role'}
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Role name"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      clearError('name');
                    }}
                    placeholder="e.g. Operations Manager"
                    {...fieldProps('name')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    select
                    label="Guard name"
                    value={guardName}
                    onChange={(e) => {
                      setGuardName(e.target.value);
                      clearError('guard_name');
                    }}
                    {...fieldProps('guard_name')}
                  >
                    {GUARD_NAME_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Permissions
              </Typography>
              {errors.permissions && (
                <Typography variant="caption" color="error" sx={{ mb: 1, display: 'block' }}>
                  {errors.permissions}
                </Typography>
              )}
              <RolePermissionsPicker
                allPermissions={allPermissions}
                selectedPermissionIds={selectedPermissionIds}
                onSelectedPermissionIdsChange={setSelectedPermissionIds}
                searchText={permissionSearch}
                onSearchTextChange={setPermissionSearch}
                loading={permissionsLoading}
              />
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={() => navigate(ROLES_LIST_PATH)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? (
                <CircularProgress size={22} color="inherit" />
              ) : isEdit ? (
                'Save changes'
              ) : (
                'Add role'
              )}
            </Button>
          </Box>
        </Box>
      </Container>
    </PageLayout>
  );
};

export default CreateRole;
